class LinePointArea extends Chart {
  constructor(config) {
    super(config);
    this.requiredProps = ['container', 'data', 'xAxis', 'yAxis'];
    this.svg;
    this.x;
    this.y;
    this.tooltip = {};
  }

  createTooltip() {
    const { svg, container } = this;
    // Add an empty tooltip bar and line
    this.tooltip.tooltipBar = svg.append('line').attr('class', 'd3-tooltip-bar');
    this.tooltip.tooltipLine = svg.append('line').attr('class', 'd3-tooltip-line');
    this.tooltip.tooltipCard = d3.select('body').append('div').attr('class', `d3-tooltip-card tooltip-card-${container.replace('#','')}`);
    return this;
  }

  displayTooltip(self) {
    // grab the location of the users cursor and convert it to a meaningful x-axis value
    const x0 = parseInt(d3.mouse(self)[0]);
    const { width, height, margin } = this.chartDimensions;
    const { data, x, tooltip } = this;
    const { tooltipBar, tooltipLine, tooltipCard } = tooltip;

    const xAxisField = this.xAxis.field || 'rdate';

    const date = x.invert(d3.mouse(self)[0]);
    const xAxisFormat = this.xAxis.format || 'MM/DD/YYYY';
    const dateFormat = this.dateConfig[xAxisFormat];

    const activeData = data.filter(d => moment(d[xAxisField]).format(xAxisFormat) === dateFormat(date));

    tooltipBar
      .attr('x1', x0)
      .attr('x2', x0)
      .attr('y1', 0)
      .attr('y2', height)
      .style('stroke', '#5d86ff')
      .style('stroke-width', '36px')
      .style('opacity', 0.1);
    
    // The tooltip line is appended to the graph when it is rendered
    // This sets the coordinates of the line as well as the display styles
    tooltipLine
      .attr('x1', x0)
      .attr('x2', x0)
      .attr('y1', 0)
      .attr('y2', height)
      .style('stroke', '#5d86ff')
      .style('stroke-width', '1px')
      .style('opacity', 0.8);
    
    if (activeData.length > 0) {      
      tooltipCard
        .style('left', d3.event.pageX + 'px')
        .style('top', d3.event.pageY - 28 + 'px')
        .style('opacity', 1)
        .html(this.setTooltipHTML(activeData));
    } else {
      this.hideTooltip();
    }
  }

  /**
   * This method is used to remove an existing Heatmap tooltip
   * @return {object} [returns Heatmap]
   */
  hideTooltip() {
    this.tooltip.tooltipCard.style('opacity', 0);
    return this;
  }

  setTooltipHTML(data) {
    const xAxisField = this.xAxis.field || 'rdate';
    const yAxisField = this.yAxis.field || 'rvalue';
    const unitsField = this.yAxis.unitsField || '';

    const xAxisFormat = this.xAxis.format || 'MM/DD/YYYY';

    if (data.length > 0) {
      let html = `<div class="d3-tooltip-title">${moment(data[0][xAxisField]).format(xAxisFormat)}</div>`;
      data.forEach( d => {
        html += `
          <div class="d3-tooltip-value-wrapper">
            <div class="d3-tooltip-value">${d[yAxisField].toLocaleString()} ${d[unitsField]}</div>
          </div>`
      });
      return html;
    } else {
      const existingHTML = document.querySelector(`.d3-tooltip-card-${this.container.replace('#','')}`).innerHTML;
      return existingHTML;
    }
  }

  createOverlay(svg, mousemove = () => {}, mouseout = () => {}) {
    // Add an overlay to the chart so we can append the tooltip lines on hover
    const self = this;
    const { width, height } = this.chartDimensions;

    const rect = svg.append('rect')
      .attr('transform', 'translate(0,0)')
      .attr('class', 'overlay')
      .style('opacity', 0)
      .attr('width', width)
      .attr('height', height)
      .on('mousemove', function () {
        // self.createTooltip(this, x, tooltipBar, tooltipLine, tooltipCard);
        self.displayTooltip(this);
        mousemove(this, self.x);
      })
      .on('mouseout', function() {
        self.hideTooltip();
        mouseout();
      });
    return this;
  }

  /**
   * This method handles all of the logic surrounding creating the graph series
   * This method creates the tooltips, area, line, and point elements
   */
  createSeries() {
    const { width, height } = this.chartDimensions;
    const { svg, data, x, y } = this;
    const self = this;

    const xAxisField = this.xAxis.field || 'rdate';
    const yAxisField = this.yAxis.field || 'rvalue';

    const area = this.createArea(xAxisField, yAxisField, x, y);
    const line = this.createLine(xAxisField, yAxisField, x, y);
    area.y0(y(y.domain()[0]));

    const tooltip = this.createTooltip();
    const gradient = this.createGradient(svg, '#EC407A', '#FFF');
    
    if (this.series.length > 1) {
      this.series.map((s) => {
        const color = s.color || '#6464c1';
        return [
          this.appendAreaSeries(svg, data, [s.field, s.value], 'd3-area', area, color),
          this.appendLineSeries(svg, data, [s.field, s.value], 'd3-line', line, color),
          this.appendPointSeries(svg, data, [s.field, s.value], 'd3-point', x, y, s.color || color),
        ];
      });      
    } else {
      const color = this.series[0].color || '#6464c1';
      const areaSeries = this.appendAreaSeries(svg, data, [], 'd3-area', area, color);
      const lineSeries = this.appendLineSeries(svg, data, [], 'd3-line', line, color);
      const pointSeries = this.appendPointSeries(svg, data, [], 'd3-point', x, y, color);
    }

    const overlay = this.createOverlay(svg, this.events.mousemove, this.events.mouseout);
    return this;
  } // createSeries()

  /**
   * This utility method is used as a shorthand for appending a line or area series to the chart
   * @param  {object} svg      [svg to append to]
   * @param  {array} data      [data to plot]
   * @param  {string} classList [list of classes to assign the series]
   * @param  {function} series    [D3 line/area constructor to be used to plot the data]
   * @return {object}           [Returns DOM/SVG node]
   */
  appendLineSeries(svg, data, filter, classList, series, color) {
    const filteredData = data.filter(d => d[filter[0]] === filter[1]);
    return svg.append('path')
      .datum(filteredData)
      .attr('class', classList)
      .attr('d', series)
      .style('fill', 'none')
      .style('stroke', color);
  } // appendLineSeries()

  /**
   * This utility method is used as a shorthand for appending a line or area series to the chart
   * @param  {object} svg      [svg to append to]
   * @param  {array} data      [data to plot]
   * @param  {string} classList [list of classes to assign the series]
   * @param  {function} series    [D3 line/area constructor to be used to plot the data]
   * @return {object}           [Returns DOM/SVG node]
   */
  appendAreaSeries(svg, data, filter, classList, series, color) {
    const filteredData = data.filter(d => d[filter[0]] === filter[1]);
    return svg.append('path')
      .datum(filteredData)
      .attr('fill', 'url(#svgGradient)')
      .attr('class', classList)
      .attr('d', series)
      .style('opacity', 0.3);
  } // appendAreaSeries()

  appendPointSeries(svg, data, filter, classList, x, y, color) {
    const filteredData = data.filter(d => d[filter[0]] === filter[1]);
    svg.selectAll(classList)
        .data(filteredData) // set data source
      .enter().append('circle')
              .attr('r', 5.5) // dot radius
              .attr('cx', function(d) { return x(new Date(`${d.rdate} 00:00:00`)) }) // dot x placement
              .attr('cy', function(d) { return y(d.rvalue); }) // dot y placement
              .attr('class', classList)
              .style('fill', color)
              .transition()
            .duration(1250)
            .style('opacity', 1);
  } // appendPointSeries()

  draw() {
    this
      .validateConfig()
      .createSVG()    
      .createScales()
      .createAxis('x', 'rdate', 'x-axis axis')
      .createAxis('y', 'rvalue', 'y-axis axis')
      .createSeries();
    return this;
  } // draw()
}
